import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type { AssetAudit, AssetAuditStatus } from '../lib/types.ts';

const STATUS_BADGE: Record<AssetAuditStatus, string> = {
  assigned: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  compliant: 'bg-emerald-50 text-emerald-700',
  not_compliant: 'bg-red-50 text-red-700',
};

const STATUS_LABEL: Record<AssetAuditStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  compliant: 'Compliant',
  not_compliant: 'Not compliant',
};

function assetLabel(audit: AssetAudit): string {
  const a = audit.asset;
  return (
    [a?.year, a?.make, a?.model].filter(Boolean).join(' ') ||
    a?.registration_plate ||
    'Asset'
  );
}

function isOpen(status: AssetAuditStatus): boolean {
  return status === 'assigned' || status === 'in_progress';
}

export function MyAudits() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-audits'],
    queryFn: () => adminApi.get<{ audits: AssetAudit[] }>('/api/audits/mine'),
  });

  if (isLoading) return <CenteredSpinner />;

  const audits = data?.audits ?? [];
  const outstanding = audits.filter((a) => isOpen(a.status));
  const completed = audits.filter((a) => !isOpen(a.status));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">My audits</h1>
        <p className="mt-1 text-sm text-slate-500">
          Audits assigned to you. Open one to complete the questionnaire and submit your responses.
        </p>
      </div>

      {audits.length === 0 ? (
        <EmptyState
          title="No audits assigned"
          description="When an administrator assigns an audit to you, it will appear here."
        />
      ) : (
        <div className="space-y-6">
          <AuditList title="To do" audits={outstanding} emptyText="Nothing outstanding — you're all caught up." />
          {completed.length > 0 && <AuditList title="Completed" audits={completed} />}
        </div>
      )}
    </div>
  );
}

function AuditList({
  title,
  audits,
  emptyText,
}: {
  title: string;
  audits: AssetAudit[];
  emptyText?: string;
}) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      {audits.length === 0 ? (
        emptyText ? <p className="text-sm text-slate-500">{emptyText}</p> : null
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100">
            {audits.map((audit) => {
              const open = isOpen(audit.status);
              return (
                <li key={audit.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">
                        {assetLabel(audit)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[audit.status]}`}
                      >
                        {STATUS_LABEL[audit.status]}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {audit.questionnaire?.name ?? 'Audit'}
                      {audit.company?.company_name ? ` · ${audit.company.company_name}` : ''}
                      {audit.assigned_at
                        ? ` · assigned ${new Date(audit.assigned_at).toLocaleDateString()}`
                        : ''}
                    </div>
                  </div>
                  <LinkBtn to={`/audit/audits/${audit.id}`} size="sm">
                    {open ? (audit.status === 'assigned' ? 'Start' : 'Continue') : 'View'}
                  </LinkBtn>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
